import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useShopsFromDb } from './useShopsFromDb';
import { uploadImageToSupabase, formatImageUploadError } from './uploadImage';
import './AppCalendarEventsPage.css';

const emptyForm = () => ({
  event_date: '',
  title: '',
  description: '',
  image_url: '',
  time_label: '',
  shop_id: '',
  published: true,
  sort_order: 0,
});

function formatDateTr(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('tr-TR', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function AppCalendarEventsPage() {
  const { rawRows: shops, loading: shopsLoading } = useShopsFromDb();
  const shopNameById = useMemo(() => {
    const m = {};
    for (const s of shops) {
      if (s?.id != null) m[String(s.id)] = (s.shop || '').trim() || `Mağaza #${s.id}`;
    }
    return m;
  }, [shops]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const formRef = useRef(null);

  const friendlyError = (msg) => {
    if (/failed to fetch|networkerror|load failed/i.test(msg)) {
      return 'Ağ hatası: İstek sunucuya ulaşamadı.';
    }
    return msg;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: qErr } = await supabase
        .from('app_calendar_events')
        .select(
          'id, event_date, title, description, image_url, time_label, shop_id, published, sort_order, created_at, updated_at',
        )
        .order('event_date', { ascending: true })
        .order('sort_order', { ascending: true });
      if (qErr) setError(friendlyError(qErr.message));
      else setRows(data || []);
    } catch (err) {
      setError(friendlyError(err?.message || 'Veriler yüklenemedi.'));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(false);
  };

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
    setError('');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      event_date: row.event_date ?? '',
      title: row.title ?? '',
      description: row.description ?? '',
      image_url: row.image_url ?? '',
      time_label: row.time_label ?? '',
      shop_id: row.shop_id != null && row.shop_id !== '' ? String(row.shop_id) : '',
      published: row.published !== false,
      sort_order: row.sort_order ?? 0,
    });
    setFormOpen(true);
    setError('');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const title = form.title.trim();
    const eventDate = form.event_date.trim();
    if (!title || !eventDate) {
      setError('Tarih ve başlık zorunludur.');
      return;
    }

    const payload = {
      event_date: eventDate,
      title,
      description: form.description.trim() || null,
      image_url: form.image_url.trim() || null,
      time_label: form.time_label.trim() || null,
      shop_id: form.shop_id === '' ? null : Number(form.shop_id),
      published: form.published,
      sort_order: Number.isFinite(Number(form.sort_order)) ? Number(form.sort_order) : 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        const { error: uErr } = await supabase
          .from('app_calendar_events')
          .update(payload)
          .eq('id', editingId);
        setSaving(false);
        if (uErr) {
          setError(friendlyError(uErr.message));
          return;
        }
      } else {
        const { error: iErr } = await supabase.from('app_calendar_events').insert(payload);
        setSaving(false);
        if (iErr) {
          setError(friendlyError(iErr.message));
          return;
        }
      }
      resetForm();
      await load();
    } catch (err) {
      setSaving(false);
      setError(friendlyError(err?.message || 'Kayıt başarısız.'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu etkinliği silmek istiyor musunuz?')) return;
    setError('');
    const { error: dErr } = await supabase.from('app_calendar_events').delete().eq('id', id);
    if (dErr) {
      setError(friendlyError(dErr.message));
      return;
    }
    if (editingId === id) resetForm();
    await load();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingImage(true);
    setError('');
    try {
      const publicUrl = await uploadImageToSupabase(file, { folder: 'calendar' });
      setForm((f) => ({ ...f, image_url: publicUrl }));
    } catch (err) {
      setError(formatImageUploadError(err));
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePasteImageUrl = async () => {
    setError('');
    try {
      if (!navigator?.clipboard?.readText) {
        throw new Error('Tarayıcı panoya erişim izni vermedi.');
      }
      const text = (await navigator.clipboard.readText()).trim();
      if (!text) {
        setError('Panoda bir link bulunamadı.');
        return;
      }
      setForm((f) => ({ ...f, image_url: text }));
    } catch (err) {
      setError(err?.message || 'Panodan link yapıştırılamadı.');
    }
  };

  return (
    <div className="admin-page-content app-cal-events-page">
      <div className="app-cal-events-header">
        <button type="button" className="app-cal-events-new-btn" onClick={startCreate}>
          Yeni etkinlik
        </button>
      </div>

      {error ? <p className="app-cal-events-banner" role="alert">{error}</p> : null}

      {formOpen ? (
        <form ref={formRef} className="app-cal-events-form" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Etkinliği düzenle' : 'Yeni etkinlik'}</h2>
          <div className="app-cal-events-form-grid">
            <label className="app-cal-events-field">
              <span>Tarih</span>
              <input
                type="date"
                value={form.event_date}
                onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
                required
              />
            </label>
            <label className="app-cal-events-field">
              <span>Sıra (aynı gün)</span>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              />
            </label>
            <label className="app-cal-events-field app-cal-events-field-span2">
              <span>Başlık</span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                required
                placeholder="Örn. Canlı müzik — Kadıköy"
              />
            </label>
            <label className="app-cal-events-field app-cal-events-field-span2">
              <span>Şube</span>
              <select
                value={form.shop_id}
                onChange={(e) => setForm((f) => ({ ...f, shop_id: e.target.value }))}
                disabled={shopsLoading}
              >
                <option value="">Tüm şubeler / genel</option>
                {shops.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.shop || `Mağaza #${s.id}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="app-cal-events-field app-cal-events-field-span2">
              <span>Saat / süre (isteğe bağlı)</span>
              <input
                type="text"
                value={form.time_label}
                onChange={(e) => setForm((f) => ({ ...f, time_label: e.target.value }))}
                placeholder="19:00–22:00"
              />
            </label>
            <label className="app-cal-events-field app-cal-events-field-span2">
              <span>Görsel</span>
              <div className="app-cal-events-image-row">
                <input
                  type="url"
                  inputMode="url"
                  value={form.image_url}
                  onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://… veya yükle"
                />
                <button type="button" className="app-cal-events-img-btn" onClick={handlePasteImageUrl}>
                  Yapıştır
                </button>
                <label className="app-cal-events-img-btn app-cal-events-img-btn--upload">
                  {uploadingImage ? '…' : 'Yükle'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={uploadingImage}
                    onChange={handleImageUpload}
                  />
                </label>
              </div>
              <span className="app-cal-events-field-hint">
                Görsel linki yapıştırın veya Supabase Storage&apos;a yükleyin.
              </span>
            </label>
            {form.image_url.trim() ? (
              <div className="app-cal-events-image-preview app-cal-events-field-span2">
                <span className="app-cal-events-field-hint">Önizleme</span>
                <img
                  src={form.image_url.trim()}
                  alt=""
                  className="app-cal-events-preview-img"
                  onError={(e) => {
                    e.currentTarget.style.visibility = 'hidden';
                  }}
                />
              </div>
            ) : null}
            <label className="app-cal-events-field app-cal-events-field-span2">
              <span>Açıklama</span>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                placeholder="Kısa açıklama veya detay"
              />
            </label>
            <label className="app-cal-events-check">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
              />
              <span>Yayında (web + uygulama listesinde görünsün)</span>
            </label>
          </div>
          <div className="app-cal-events-form-actions">
            <button type="submit" className="app-cal-events-save" disabled={saving}>
              {saving ? 'Kaydediliyor…' : editingId ? 'Güncelle' : 'Kaydet'}
            </button>
            <button type="button" className="app-cal-events-cancel" onClick={resetForm} disabled={saving}>
              İptal
            </button>
          </div>
        </form>
      ) : null}

      {loading ? (
        <p className="app-cal-events-muted">Yükleniyor…</p>
      ) : rows.length === 0 ? (
        <p className="app-cal-events-muted">Henüz etkinlik yok. «Yeni etkinlik» ile ekleyin.</p>
      ) : (
        <div className="app-cal-events-table-wrap">
          <table className="app-cal-events-table">
            <thead>
              <tr>
                <th>Görsel</th>
                <th>Tarih</th>
                <th>Başlık</th>
                <th>Şube</th>
                <th>Yayın</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="app-cal-events-thumb-cell">
                    {r.image_url?.trim() ? (
                      <img
                        src={r.image_url.trim()}
                        alt=""
                        className="app-cal-events-thumb"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="app-cal-events-thumb-placeholder">—</span>
                    )}
                  </td>
                  <td>{formatDateTr(r.event_date)}</td>
                  <td>
                    <strong>{r.title}</strong>
                    {r.time_label ? <span className="app-cal-events-meta"> · {r.time_label}</span> : null}
                  </td>
                  <td>
                    {r.shop_id != null && r.shop_id !== ''
                      ? (shopNameById[String(r.shop_id)] || `ID ${r.shop_id}`)
                      : '—'}
                  </td>
                  <td>{r.published ? 'Evet' : 'Hayır'}</td>
                  <td className="app-cal-events-actions">
                    <button type="button" onClick={() => startEdit(r)}>Düzenle</button>
                    <button type="button" className="danger" onClick={() => handleDelete(r.id)}>Sil</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
